import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';
import chevronDownIcon from '../assets/icons/chevron-down.svg';
import logoutIcon from '../assets/icons/log-out.svg';
import usersIcon from '../assets/icons/users.svg';
import caissesIcon from '../assets/icons/caisses.svg';
import categoriesIcon from '../assets/icons/categories.svg';

const LoggedUserCard = () => {
  const history = useHistory();
  const { user, logout } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState(null);

  // Fetch full profile information
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await authAPI.getCurrentUser();
        setProfile(response.data);
      } catch (error) {
        console.error('Error fetching profile:', error);
        // Fallback to basic user info if profile fetch fails
        setProfile(user);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchProfile();
    }
  }, [user]);

  // Use profile data if available, otherwise fallback to basic user info
  const fullName = profile?.firstName && profile?.lastName 
    ? `${profile.firstName} ${profile.lastName}` 
    : user?.username || '—';
  const initials = profile?.firstName && profile?.lastName
    ? `${profile.firstName[0]}${profile.lastName[0]}`.toUpperCase()
    : user?.username?.[0]?.toUpperCase() || '•';
  const getDisplayRole = (role) => {
    const roleMap = {
      'MANAGER': 'Manager',
      'AGENT': 'Agent de Caisse', 
      // 'VERIFICATION': 'Agent de Pointage', // Legacy role - commented out after migration
      'POINTAGE_CLIENT': 'Agent de Pointage Client',
      'POINTAGE_COMPTABILITE': 'Agent de Pointage Comptabilité'
    };
    return roleMap[role] || role || '';
  };
  
  const displayRole = getDisplayRole(profile?.role || user?.role || '');

  const toggleDropdown = () => {
    if (isDropdownOpen) setDropdownPosition(null);
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleLogout = () => {
    logout();
    history.push('/login');
  };

  const handleNavigate = (path) => {
    setIsDropdownOpen(false);
    history.push(path);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedDropdown = dropdownRef.current && dropdownRef.current.contains(event.target);
      const clickedTrigger = triggerRef.current && triggerRef.current.contains(event.target);
      if (!clickedDropdown && !clickedTrigger) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (isDropdownOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right
      });
    }
  }, [isDropdownOpen]);

  if (loading) {
    return (
      <div className="flex items-center space-x-2 px-4 py-1 rounded-lg backdrop-blur">
        <div className="flex items-center justify-center w-8 h-8 bg-gray-300 rounded-full mr-2 animate-pulse"></div>
        <div className="animate-pulse">
          <div className="h-4 bg-gray-300 rounded w-20 mb-1"></div>
          <div className="h-3 bg-gray-300 rounded w-16"></div>
        </div>
      </div>
    );
  }

  return (
    <div ref={triggerRef} className="relative">
      <div
        className="flex items-center px-4 py-1 rounded-lg backdrop-blur cursor-pointer"
        onClick={toggleDropdown}
      >
        {/* User Circle */}
        <div className="flex items-center justify-center w-8 h-8 bg-[#027BC6] text-white rounded-full font-bold text-sm mr-2">
          {initials}
        </div>
        
        {/* User Name and Role */}
        <div>
          <p className="m-0 text-sm font-bold text-[#38485C]">{fullName}</p>
          <p className="m-0 text-xs font-semibold text-[#4B4B4B]">{displayRole}</p>
        </div>
        
        {/* Spacer */}
        <div className="w-4"></div>
        
        {/* Chevron Down Icon */}
        <img 
          src={chevronDownIcon} 
          alt="Chevron Down" 
          className={`h-3 w-3 transform transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} 
        />
      </div>

      {/* Dropdown Menu */}
      {isDropdownOpen && dropdownPosition && createPortal(
        <div
          ref={dropdownRef}
          className="bg-white shadow-lg rounded-lg border border-[#F7F7F7] py-1"
          style={{
            position: 'fixed',
            top: dropdownPosition.top,
            right: dropdownPosition.right,
            zIndex: 1500,
            minWidth: '220px'
          }}
        >
          {user?.role === 'MANAGER' && (
            <>
              <button
                onClick={() => handleNavigate('/users')}
                className="flex items-center w-full px-4 py-2 text-[14px] font-semibold hover:bg-gray-100 whitespace-nowrap text-left"
                style={{ color: '#385576' }}
              >
                <img src={usersIcon} alt="" className="w-4 h-4 mr-3 flex-shrink-0" style={{ filter: 'brightness(0) saturate(100%) invert(27%) sepia(25%) saturate(900%) hue-rotate(176deg) brightness(92%) contrast(90%)' }} />
                Gestion des utilisateurs
              </button>
              <div style={{ height: '1px', backgroundColor: '#38485C', opacity: 0.07, margin: '0 16px' }} />
              <button
                onClick={() => handleNavigate('/caisses')}
                className="flex items-center w-full px-4 py-2 text-[14px] font-semibold hover:bg-gray-100 whitespace-nowrap text-left"
                style={{ color: '#385576' }}
              >
                <img src={caissesIcon} alt="" className="w-4 h-4 mr-3 flex-shrink-0" style={{ filter: 'brightness(0) saturate(100%) invert(27%) sepia(25%) saturate(900%) hue-rotate(176deg) brightness(92%) contrast(90%)' }} />
                Gestion des caisses
              </button>
              <div style={{ height: '1px', backgroundColor: '#38485C', opacity: 0.07, margin: '0 16px' }} />
              <button
                onClick={() => handleNavigate('/categories')}
                className="flex items-center w-full px-4 py-2 text-[14px] font-semibold hover:bg-gray-100 whitespace-nowrap text-left"
                style={{ color: '#385576' }}
              >
                <img src={categoriesIcon} alt="" className="w-4 h-4 mr-3 flex-shrink-0" style={{ filter: 'brightness(0) saturate(100%) invert(27%) sepia(25%) saturate(900%) hue-rotate(176deg) brightness(92%) contrast(90%)' }} />
                Gestion des catégories
              </button>
              <div style={{ height: '1px', backgroundColor: '#38485C', opacity: 0.07, margin: '0 16px' }} />
            </>
          )}
          <button
            onClick={handleLogout}
            className="flex items-center w-full px-4 py-2 text-[14px] font-semibold hover:bg-gray-100 whitespace-nowrap text-left"
            style={{ color: '#EF476F' }}
          >
            <img src={logoutIcon} alt="" className="w-4 h-4 mr-3 flex-shrink-0" />
            Se déconnecter
          </button>
        </div>,
        document.body
      )}
    </div>
  );
};

export default LoggedUserCard;
